import { ArticleBusinessType } from 'admin/articleService'
import { CategoryFromApi } from 'products/types'
import { getProductName } from 'products/productTable/utils'
import { getBreadcrumbs } from 'products/redux'
import { PRODUCTS } from 'capacity/copy'

export type CategoryNames = Parameters<typeof getBreadcrumbs>[0]

export const categoryNamesFrom = (categories: CategoryFromApi[]): CategoryNames =>
    Object.fromEntries(
        categories.map((category) => [
            category.id,
            { name: getProductName(category.name), parentId: category.parentCategory?.id },
        ]),
    )

export function productBreadcrumb(categoryIds: string[], categoryNames: CategoryNames): string {
    const paths = categoryIds.map((id) => getBreadcrumbs(categoryNames, id)).filter((path) => path.length > 0)
    if (paths.length === 0) {
        return PRODUCTS.breadcrumb.none
    }
    if (paths.length === 1) {
        return paths[0].join(PRODUCTS.breadcrumb.separator)
    }
    return PRODUCTS.breadcrumb.many(paths.length)
}

export const PRODUCT_TYPE_LABEL: Record<ArticleBusinessType, { one: string; many: string }> = {
    single_visit_ticket: { one: 'Ticket', many: 'Tickets' },
    multi_visit_ticket: { one: 'Multi-visit ticket', many: 'Multi-visit tickets' },
    visit_reservation: { one: 'Reservation', many: 'Reservations' },
    upsell: { one: 'Upsell', many: 'Upsells' },
    service: { one: 'Service', many: 'Services' },
    service_date_change: { one: 'Date change', many: 'Date changes' },
    service_fee: { one: 'Service fee', many: 'Service fees' },
    bundle: { one: 'Bundle', many: 'Bundles' },
    season_pass: { one: 'Season pass', many: 'Season passes' },
    membership: { one: 'Membership', many: 'Memberships' },
    voucher: { one: 'Voucher', many: 'Vouchers' },
    donation: { one: 'Donation', many: 'Donations' },
    loyalty_card: { one: 'Loyalty card', many: 'Loyalty cards' },
    food_and_beverage: { one: 'Food and drink', many: 'Food and drink' },
}

export const OPTION_TYPE_LABEL = { one: 'Option', many: 'Options' }

const FALLBACK_TYPE_LABEL = { one: 'Product', many: 'Products' }

export const isKnownBusinessType = (value: string | null | undefined): value is ArticleBusinessType =>
    !!value && Object.prototype.hasOwnProperty.call(PRODUCT_TYPE_LABEL, value)

export const businessTypeLabel = (businessType: string | null | undefined): { one: string; many: string } =>
    isKnownBusinessType(businessType) ? PRODUCT_TYPE_LABEL[businessType] : FALLBACK_TYPE_LABEL

export const productTypeLabel = (businessType: ArticleBusinessType | null, optionGroupName?: string): string => {
    if (optionGroupName) {
        return `${OPTION_TYPE_LABEL.one} in ${optionGroupName}`
    }
    return businessTypeLabel(businessType).one
}

export const PASS_TYPES: ArticleBusinessType[] = ['membership', 'season_pass']

export const isPassType = (businessType: ArticleBusinessType | null | undefined): boolean =>
    !!businessType && PASS_TYPES.includes(businessType)
